import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, DollarSign, Briefcase, TrendingUp, RefreshCw, Loader2, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
      </div>
      <span className="text-xs sm:text-sm text-gray-500 font-medium">{label}</span>
    </div>
    <p className="text-xl sm:text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
  </div>
);

const AdsDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("30");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const getPSTDate = (date = new Date()) => {
    return new Date(date.toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
  };

  const formatDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const getDateRange = () => {
    const pstNow = getPSTDate();
    const todayStr = formatDate(pstNow);
    if (dateRange === "today") {
      return { start_date: todayStr, end_date: todayStr };
    }
    if (dateRange === "yesterday") {
      const y = new Date(pstNow);
      y.setDate(y.getDate() - 1);
      return { start_date: formatDate(y), end_date: formatDate(y) };
    }
    if (dateRange === "custom" && customStart && customEnd) {
      return { start_date: customStart, end_date: customEnd };
    }
    const days = parseInt(dateRange);
    const start = new Date(pstNow);
    start.setDate(start.getDate() - days);
    return { start_date: formatDate(start), end_date: todayStr };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const { start_date, end_date } = getDateRange();
      const res = await axios.get(`${API}/google-ads/cost-per-job`, {
        params: { start_date, end_date }
      });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Google Ads data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fmtDollar = (n) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button onClick={() => navigate("/h2g")} variant="ghost" size="sm" className="flex items-center gap-1 px-2 sm:px-3">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div className="border-l border-gray-300 h-6 hidden sm:block" />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  Google Ads Analytics
                </h1>
              </div>
            </div>
            <Button onClick={fetchData} variant="outline" size="sm" disabled={loading} data-testid="refresh-ads">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Date Range Selector */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {[
            { val: "today", label: "Today" },
            { val: "yesterday", label: "Yesterday" },
            { val: "7", label: "7D" },
            { val: "14", label: "14D" },
            { val: "30", label: "30D" },
            { val: "60", label: "60D" },
            { val: "90", label: "90D" },
          ].map(({ val, label }) => (
            <button
              key={val}
              onClick={() => setDateRange(val)}
              className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                dateRange === val
                  ? "bg-blue-600 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
              data-testid={`range-${val}`}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => setDateRange("custom")}
            className={`px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              dateRange === "custom"
                ? "bg-blue-600 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
          >
            Custom
          </button>
          {dateRange === "custom" && (
            <div className="flex items-center gap-2 flex-wrap">
              <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <span className="text-gray-400">to</span>
              <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <Button size="sm" onClick={fetchData}>Go</Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
          </div>
        ) : data ? (
          <>
            {/* Top Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
              <StatCard icon={DollarSign} label="Total Spend" value={fmtDollar(data.total_ad_spend)} sub={`Tracked: ${fmtDollar(data.total_tracked_spend)}`} color="bg-blue-600" />
              <StatCard icon={Briefcase} label="Jobs Booked" value={data.total_jobs} color="bg-emerald-600" />
              <StatCard icon={TrendingUp} label="Cost / Job" value={data.total_jobs > 0 ? fmtDollar(data.cost_per_job) : "N/A"} sub="Tracked spend only" color="bg-orange-500" />
            </div>

            {/* State Breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">Cost Per Job by State</h2>
                <p className="text-sm text-gray-500">{data.start_date} to {data.end_date}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="state-breakdown-table">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 sm:px-6 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider">State</th>
                      <th className="px-4 sm:px-6 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider text-right">Ad Spend</th>
                      <th className="px-4 sm:px-6 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider text-right">Jobs</th>
                      <th className="px-4 sm:px-6 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider text-right">Cost/Job</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.state_breakdown?.map((row, i) => (
                      <tr key={row.state} className="hover:bg-gray-50" data-testid={`state-row-${i}`}>
                        <td className="px-4 sm:px-6 py-3 font-semibold text-gray-900">{row.state}</td>
                        <td className="px-4 sm:px-6 py-3 text-right text-gray-700">{fmtDollar(row.cost)}</td>
                        <td className="px-4 sm:px-6 py-3 text-right">
                          <span className={`font-semibold ${row.jobs > 0 ? "text-emerald-600" : "text-gray-400"}`}>
                            {row.jobs || "0"}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-right">
                          <span className={`font-bold ${row.cost_per_job ? "text-orange-600" : "text-gray-400"}`}>
                            {row.cost_per_job ? fmtDollar(row.cost_per_job) : "—"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Campaign Breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">Campaign Breakdown</h2>
                <p className="text-sm text-gray-500">{data.campaign_breakdown?.length || 0} campaigns</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="campaign-breakdown-table">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 sm:px-6 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider">Campaign</th>
                      <th className="px-4 sm:px-6 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider text-center">State</th>
                      <th className="px-4 sm:px-6 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wider text-right">Spend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.campaign_breakdown?.map((campaign, i) => (
                      <tr key={i} className={`hover:bg-gray-50 ${!campaign.tracked ? "opacity-40" : ""}`} data-testid={`campaign-row-${i}`}>
                        <td className="px-4 sm:px-6 py-3 font-medium text-gray-900 max-w-xs truncate">{campaign.campaign_name}</td>
                        <td className="px-4 sm:px-6 py-3 text-center">
                          {campaign.states?.length > 0 ? (
                            <span className="inline-flex gap-1 flex-wrap justify-center">
                              {campaign.states.map(s => (
                                <span key={s} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-semibold">{s}</span>
                              ))}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-right text-gray-700 font-medium">{fmtDollar(campaign.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-24 text-gray-500">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium">No data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdsDashboard;
